import classNames from "classnames";
import classes from "./CampaignRecommendations.module.less";
import { Link } from "~/common/components/Link";
import { useTranslation } from "react-i18next";
import { ProductService } from "~/AddToCart/ProductService";
import { getInitialPriceReference } from "~/AddToCart/redux/addToCart/getInitialPriceReference";
import { calculateSalesCampaign4PDP } from "~/SalesCampaign/utils";
import type { SrpProductData } from "~/utils/models";
import { SALES_CAMPAIGN_PAGE } from "~/utils/url/staticUrls";
import {
  ProductRecommendationSection,
  ProductRecommendationTitle,
  ProductRecoType,
} from "~/feature/SaleRecommendations/components/ProductRecommendationSection/ProductRecommendationSection";
import { type ProductData } from "~/models/ProductData";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { Skeleton } from "@mui/material";
import { FONT_RAW_SIZES, FontSize } from "~/common/components/materialInput/Fonts";

export interface SimilarSalesProps {
  className?: string;
  products?: SrpProductData[];
  variant: "SRP" | "PDP";
  loading?: boolean;
}

export const CampaignRecommendations = ({ className, products, variant, loading }: SimilarSalesProps) => {
  const { t } = useTranslation();
  // As per SCHAEFF-33361, Max 4 products
  const productCodes = products?.slice(0, 4).map((product) => product.code) ?? [];

  let productDescriptors = Object.values(
    ProductService.useMultipleProductDescriptors({
      productCodes,
      priceReference: getInitialPriceReference(),
      options: { fetch: true },
    }),
  );

  const activeSalesCampaignUrl = getActiveCampaign()?.urlMapping;
  const pageUrl = [SALES_CAMPAIGN_PAGE, activeSalesCampaignUrl].filter(Boolean).join("/");

  productDescriptors = productDescriptors.filter((productDescriptor) => {
    const salesMap = calculateSalesCampaign4PDP({ productDescriptor });
    if (salesMap.status !== "loaded") {
      return true;
    }
    const { salePerPack } = salesMap.data;
    const entries = Object.entries(salePerPack);
    return entries.length > 0;
  });

  if (!productDescriptors.length && !loading) {
    return null;
  }

  const getTrackingAttributes = (product: ProductData, productDescriptor: ProductDescriptor) => {
    const salesMap = calculateSalesCampaign4PDP({
      productDescriptor,
    });
    const salesCampaignCode = salesMap.data?.salePerPack
      ? Object.values(salesMap.data?.salePerPack)[0]?.campaignCode
      : null;
    const trackingInfix = variant === "PDP" ? "spos" : "cob";
    const rootTrackingAttributes = {
      [`tracking-aux-${trackingInfix}-product-parent-marker`]: "true",
      [`tracking-aux-${trackingInfix}-is-campaign-belonging`]: salesCampaignCode ? "true" : false,
      [`tracking-aux-${trackingInfix}-campaign`]: salesCampaignCode ?? "None",
    };
    const linkProps = {
      [`tracking-aux-${trackingInfix}-product-name`]: product.designation,
      [`tracking-aux-${trackingInfix}-product-id`]: product.sapMaterialNumber,
      target: "_blank",
    };
    const buttonProps = {
      [`tracking-aux-${trackingInfix}-add-to-cart-button`]: "true",
      reco_type: "spos",
    };
    return { rootTrackingAttributes, linkProps, buttonProps };
  };

  let title = (
    <ProductRecommendationTitle>
      {t(`feature.salesPush.similarProducts.title.${variant.toLowerCase()}`)}
    </ProductRecommendationTitle>
  );
  if (loading) {
    title = <Skeleton height={FONT_RAW_SIZES[FontSize.HeadlineSmall].fontSize} width={200} className={classes.title} />;
  }

  return (
    <ProductRecommendationSection
      className={classNames(className)}
      title={
        <div className={classes.titleRow}>
          {title}
          {!loading && (
            <Link href={pageUrl} target={"_blank"} tracking-aux-cob-show-all-products-link-marker={"true"}>
              {t("feature.salesPush.similarProducts.viewAll")}
            </Link>
          )}
        </div>
      }
      loading={loading}
      loadingLabel={t("feature.salesPush.similarProducts.loading")}
      products={productCodes}
      variant={ProductRecoType[`campaign${variant}`]}
      getTrackingAttributes={getTrackingAttributes}
    />
  );
};

function getActiveCampaign(): SalesCampaignDTO | undefined {
  return (window.SCHA.activeSalesCampaigns ?? [])[0];
}
